import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Database,
  AlertTriangle,
  MessageSquare,
  Mic,
  Volume2,
  FileText,
  HardDrive,
  Code,
} from 'lucide-react';
import {
  mockNeMo26InstanceDetails,
  NeMoDataset,
  NeMoTaskType,
} from '@/mock/gpu-instance';
import { NeMoDatasetManager, NeMoDatasetCard } from '../../components/nemo';

const NeMoDatasetsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedDataset, setSelectedDataset] = useState<NeMoDataset | null>(null);
  const [datasetCardOpen, setDatasetCardOpen] = useState(false);
  const [datasetCardDataset, setDatasetCardDataset] = useState<NeMoDataset | null>(null);

  const details = useMemo(() => mockNeMo26InstanceDetails(instanceId || ''), [instanceId]);

  const handleViewDatasetCard = (dataset: NeMoDataset) => {
    setDatasetCardDataset(dataset);
    setDatasetCardOpen(true);
  };

  const getTaskInfo = (task: NeMoTaskType) => {
    switch (task) {
      case 'llm':
        return {
          name: 'LLM',
          icon: MessageSquare,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
        };
      case 'asr':
        return {
          name: 'ASR',
          icon: Mic,
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
        };
      case 'tts':
        return {
          name: 'TTS',
          icon: Volume2,
          color: 'text-purple-500',
          bgColor: 'bg-purple-500/10',
        };
    }
  };

  // Statistics
  const llmDatasets = details.datasets.filter(d => d.task === 'llm').length;
  const asrDatasets = details.datasets.filter(d => d.task === 'asr').length;
  const ttsDatasets = details.datasets.filter(d => d.task === 'tts').length;

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-blue-500" />
            NeMo Datasets
          </h1>
          <p className="text-muted-foreground">
            Manage training datasets for NeMo 2.6.1
          </p>
        </div>
      </div>

      {/* Dataset Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{details.datasets.length}</div>
            <div className="text-sm text-muted-foreground">Total Datasets</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{llmDatasets}</div>
            <div className="text-sm text-muted-foreground">LLM</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{asrDatasets}</div>
            <div className="text-sm text-muted-foreground">ASR</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{ttsDatasets}</div>
            <div className="text-sm text-muted-foreground">TTS</div>
          </CardContent>
        </Card>
      </div>

      {/* Dataset Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Datasets</CardTitle>
          <CardDescription>Browse and select datasets for your experiments</CardDescription>
        </CardHeader>
        <CardContent>
          <NeMoDatasetManager
            datasets={details.datasets}
            selectedDatasetId={selectedDataset?.id}
            onSelectDataset={setSelectedDataset}
            onViewDatasetCard={handleViewDatasetCard}
          />
        </CardContent>
      </Card>

      {/* Selected Dataset Details */}
      {selectedDataset && (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${getTaskInfo(selectedDataset.task).bgColor}`}>
                {(() => {
                  const Icon = getTaskInfo(selectedDataset.task).icon;
                  return <Icon className={`h-5 w-5 ${getTaskInfo(selectedDataset.task).color}`} />;
                })()}
              </div>
              <div>
                <CardTitle>{selectedDataset.displayName}</CardTitle>
                <CardDescription>{selectedDataset.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Database className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Train Samples</div>
                <div className="font-mono font-medium">{selectedDataset.trainCount.toLocaleString()}</div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Database className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Val Samples</div>
                <div className="font-mono font-medium">{selectedDataset.valCount.toLocaleString()}</div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <HardDrive className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Total Size</div>
                <div className="font-mono font-medium">{selectedDataset.totalSize}</div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <FileText className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                <div className="text-xs text-muted-foreground">Format</div>
                <div className="font-mono font-medium text-xs">
                  {selectedDataset.format === 'jsonl' ? 'JSONL' : 'JSON Manifest'}
                </div>
              </div>
            </div>

            {/* Format Example */}
            <div>
              <div className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <Code className="h-4 w-4" />
                Format Example
              </div>
              <pre className="p-3 bg-muted rounded text-xs font-mono overflow-x-auto">
                {selectedDataset.formatExample}
              </pre>
            </div>

            {/* Path */}
            <div className="p-3 bg-muted/50 rounded">
              <div className="text-sm text-muted-foreground mb-1">Dataset Path</div>
              <code className="text-sm font-mono">{selectedDataset.path}</code>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Format Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Dataset Format Guide
          </CardTitle>
          <CardDescription>Required formats for NeMo training datasets</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* JSONL Format */}
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                <MessageSquare className="h-3 w-3 mr-1" />
                LLM
              </Badge>
              <span className="font-medium">JSONL Format</span>
            </div>
            <pre className="p-3 bg-muted rounded text-xs font-mono overflow-x-auto mb-2">
{`{"messages": [{"role": "system", "content": "..."}, {"role": "user", "content": "..."}, {"role": "assistant", "content": "..."}]}
{"messages": [{"role": "user", "content": "..."}, {"role": "assistant", "content": "..."}]}`}
            </pre>
            <p className="text-sm text-muted-foreground">
              Each line is a JSON object with a "messages" array. System messages are optional.
            </p>
          </div>

          {/* JSON Manifest Format */}
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
                <Mic className="h-3 w-3 mr-1" />
                ASR
              </Badge>
              <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                <Volume2 className="h-3 w-3 mr-1" />
                TTS
              </Badge>
              <span className="font-medium">JSON Manifest Format</span>
            </div>
            <pre className="p-3 bg-muted rounded text-xs font-mono overflow-x-auto mb-2">
{`{"audio_filepath": "/path/to/audio.wav", "text": "transcription text", "duration": 12.5}
{"audio_filepath": "/path/to/audio2.wav", "text": "another transcription", "duration": 8.3, "speaker": 0}`}
            </pre>
            <p className="text-sm text-muted-foreground">
              Each line describes an audio file with its transcription/text and duration. TTS datasets include speaker ID.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Dataset Card Dialog */}
      <NeMoDatasetCard
        dataset={datasetCardDataset}
        open={datasetCardOpen}
        onOpenChange={setDatasetCardOpen}
        onSelectDataset={(dataset) => {
          setSelectedDataset(dataset);
          setDatasetCardOpen(false);
        }}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> These are sample datasets.
          In production, you would upload your own datasets in the required format.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default NeMoDatasetsPage;
